// @flow

import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {IconButton, makeStyles} from '@material-ui/core';
import {CancelOutlined} from '@material-ui/icons';
import CustomButton from '../CustomButton/CustomButton';
import {colors} from '../../assets/styles/colors';

import type {Node} from 'react';
import type {Theme} from '@material-ui/core';

type Props = {
  disabled?: boolean,
  position: number,
  children?: Node,
  image?: string,
  action?: (...props: any) => Node,
  onImageDelete?: (idx: number) => void,
  onImageChange: (
    e: SyntheticInputEvent<HTMLInputElement>,
    idx: number
  ) => Promise<any>,
  height?: number,
};

const useStyles = props =>
  makeStyles((theme: Theme) => ({
    root: {
      width: '100%',
      height: props.height,
    },
    button: {
      borderRadius: theme.spacing(1.5),
      height: '100%',
      width: '100%',
      padding: 0,
      '& span': {
        height: '100%',
      },
    },
    imageBox: {
      height: props.height,
      border: `2px dashed ${colors.grey500}`,
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      borderRadius: theme.borderRadius,
      padding: theme.spacing(2),
      textAlign: 'center',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    imageBoxDashed: {
      height: props.height,
      width: '100%',
      border: `2px dashed ${colors.grey500}`,
      backgroundColor: '#FFFFFF',
      boxSizing: 'border-box',
      borderRadius: '12px',
      padding: theme.spacing(3, 2),
      textAlign: 'center',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      color: colors.purple3,
      '&:focus-within': {
        border: `2px dashed ${theme.palette.primary.main}`,
      },
    },
    image: {
      maxWidth: '100%',
    },
    removeImageIcon: {
      position: 'absolute',
      top: 4,
      right: 4,
    },
  }));

export default function UploadImageButton(props: Props) {
  const {
    disabled = false,
    height = 320,
    children,
    position,
    image,
    onImageChange,
    onImageDelete,
    action,
  } = props;

  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const handleAddImageClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.value = '';
      inputFileRef.current.click();
    }
  };

  const classes = useStyles({height})();

  const handleDelete = e => {
    if (onImageDelete) {
      onImageDelete(position);
    }
  };

  const handleChange = e => {
    onImageChange(e, position);
  };

  if (image) {
    return (
      <div className={classes.imageBox}>
        {!!action ? (
          action({handleDelete})
        ) : (
          <IconButton
            aria-label="cancel"
            className={classes.removeImageIcon}
            onClick={handleDelete}
          >
            <CancelOutlined />
          </IconButton>
        )}
        <img className={classes.image} src={image} alt="compressed" />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <CustomButton
        disabled={disabled}
        className={classes.button}
        onClick={handleAddImageClick}
      >
        <div className={classes.imageBoxDashed}>
          <div>{children}</div>
        </div>
      </CustomButton>
      <input
        ref={inputFileRef}
        hidden
        accept="image/png, image/jpeg"
        type="file"
        onChange={handleChange}
      />
    </div>
  );
}

UploadImageButton.propTypes = {
  image: PropTypes.string,
  children: PropTypes.node,
  action: PropTypes.func,
  height: PropTypes.number,
  onImageDelete: PropTypes.func,
  onImageChange: PropTypes.func.isRequired,
};
